
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Shield, User } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useNavigate } from "react-router-dom";
import { useSession } from "@supabase/auth-helpers-react";
import { useToast } from "@/hooks/use-toast";

interface AdminToggleProps {
  isAdmin: boolean;
}

export function AdminToggle({ isAdmin }: AdminToggleProps) {
  const navigate = useNavigate();
  const session = useSession();
  const { toast } = useToast();
  
  // Initialize admin view state based on session storage
  const [isAdminView, setIsAdminView] = useState(() => {
    return sessionStorage.getItem('adminViewEnabled') === 'true';
  });

  // Effect to update state when sessionStorage changes
  useEffect(() => {
    const checkAdminView = () => {
      const adminViewEnabled = sessionStorage.getItem('adminViewEnabled') === 'true';
      setIsAdminView(adminViewEnabled);
    };

    // Check on mount
    checkAdminView();
    
    // Add event listener for storage changes
    window.addEventListener('storage', checkAdminView);
    
    return () => {
      window.removeEventListener('storage', checkAdminView);
    };
  }, []);

  // If not an admin, don't show anything
  if (!isAdmin) return null;

  const handleToggle = () => {
    const newState = !isAdminView;
    
    // Update state
    setIsAdminView(newState);
    
    // Store in sessionStorage and dispatch storage event for other components
    sessionStorage.setItem('adminViewEnabled', newState ? 'true' : 'false');
    
    // Dispatch a storage event for other components to pick up
    window.dispatchEvent(new Event('storage'));
    
    toast({
      title: newState ? "Admin View Enabled" : "User View Enabled",
      description: newState ? "You are now viewing the admin dashboard" : "You are now viewing the user interface",
    });
    
    // Force a page reload to ensure all components pick up the new state
    window.location.reload();
  };

  return (
    <div className="space-y-4 pb-6 border-b border-app-border">
      <div className="flex flex-col items-center space-y-4">
        <h2 className="text-xl font-semibold text-app-text-primary">Administrator Access</h2>
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 ${!isAdminView ? 'text-app-text-primary font-medium' : 'text-app-text-secondary'}`}>
            <User className="w-5 h-5" />
            <span>User View</span>
          </div>
          
          <Switch 
            checked={isAdminView} 
            onCheckedChange={handleToggle}
            className="data-[state=checked]:bg-red-600"
          />
          
          <div className={`flex items-center gap-2 ${isAdminView ? 'text-red-600 font-medium' : 'text-app-text-secondary'}`}>
            <Shield className="w-5 h-5" />
            <span>Admin View</span>
          </div>
        </div>
        
        <Button
          onClick={handleToggle}
          className={isAdminView ? 
            "bg-red-600 hover:bg-red-700 text-white" : 
            "bg-grace-dark hover:bg-grace-dark/90 text-white"
          }
          size="lg"
        >
          {isAdminView ? (
            <>
              <User className="w-5 h-5 mr-2" />
              Switch to User View
            </>
          ) : (
            <>
              <Shield className="w-5 h-5 mr-2" />
              Switch to Admin View
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
